import { FC, useMemo, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import { Flex } from '@chakra-ui/react';

import { Scope } from 'common/hooks/useAuthenticatedAction/types';

import { CreditCard } from '../../../../hooks/useCreditCards';
import Activate from './Activate';
import CardDetailsPasscode from './CardDetailsPasscode';
import { CardDetailsContext, emptyDetails } from './context';
import DeactivateVirtualCardConfirm from './DeactivateVirtualCardConfirm';
import DeactivationDate from './DeactivationDate';
import { useResetDetailsOnCardChange } from './hooks';
import LockOptions from './LockOptions';
import OperationList from './OperationList';
import { CardDetails, OperationId, PaymentCardSidebarParams } from './types';
import UnblockPin from './UnblockPin';
import UpdateLimits from './UpdateLimits';
import UpdatePin from './UpdatePin';
import UpdateStatusConfirm from './UpdateStatusConfirm';

interface PaymentCardSidebarProps {
  creditCard: CreditCard;
}

const PaymentCardSidebar: FC<PaymentCardSidebarProps> = ({ creditCard }) => {
  const { operationId } = useParams<PaymentCardSidebarParams>();
  const [cardDetails, setCardDetails] = useState<CardDetails>(emptyDetails);

  useResetDetailsOnCardChange(setCardDetails, creditCard.creditCardId);

  const content = useMemo(() => {
    switch (operationId as OperationId) {
      case undefined:
        return <OperationList creditCard={creditCard} />;
      case 'activate':
        return <Activate creditCard={creditCard} />;
      case 'lock':
        return <LockOptions creditCard={creditCard} />;
      case 'lock-confirm':
      case 'lost-confirm':
      case 'stolen-confirm':
      case 'unlock-confirm':
        return <UpdateStatusConfirm creditCard={creditCard} />;
      case 'deactivate-confirm':
        return <DeactivateVirtualCardConfirm creditCard={creditCard} />;
      case 'atm-limit':
      case 'payment-limit':
        return <UpdateLimits creditCard={creditCard} />;
      case 'unblock-pin':
        return <UnblockPin creditCard={creditCard} />;
      case 'update-pin':
        return <UpdatePin creditCard={creditCard} />;
      case 'order-new-physical':
        return <Redirect to="/cards/order-new-physical" />;
      case 'request-new-physical':
        return <Redirect to="/cards/request-new" />;
      case 'show-details':
      case 'copy-details':
        return (
          <CardDetailsPasscode
            creditCardId={creditCard.creditCardId}
            scope={Scope.BANK_PAYMENT_CARDS_READ}
          />
        );
      case 'deactivation-date':
        return <DeactivationDate creditCard={creditCard} />;
      default:
        return <Redirect to={`/cards/${creditCard.creditCardId}`} />;
    }
  }, [creditCard, operationId]);

  return (
    <CardDetailsContext.Provider value={{ cardDetails, setCardDetails }}>
      <Flex
        flexDirection="column"
        height="100%"
        paddingX="space-24"
        paddingY="space-16"
      >
        {content}
      </Flex>
    </CardDetailsContext.Provider>
  );
};

export default PaymentCardSidebar;
